import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PhotoIcon,
  XMarkIcon,
  CalendarIcon,
  TagIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { getImageUrlWithEnv } from '../../utils/imageUrl';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = 'https://fciveria.ge';
  return `${baseUrl}${endpoint}`;
};

const PhotoGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ყველა');
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState(['ყველა']);
  const [loading, setLoading] = useState(true);
  const [updatedViews, setUpdatedViews] = useState(new Set());

  useEffect(() => {
    loadPhotos();
    loadCategories();
    
    // Set up periodic polling to refresh view counts every 10 seconds
    const interval = setInterval(() => {
      loadPhotos(false);
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const loadPhotos = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await fetch(getApiUrl('/api/photos'));
      if (response.ok) {
        const photosData = await response.json();
        // Filter out "გულშემატკივრები" category and convert backend format to frontend format
        const formattedPhotos = photosData
          .filter(photo => photo.category !== 'გულშემატკივრები')
          .map(photo => ({
            id: photo.id,
            src: photo.imageUrl,
            alt: photo.title,
            category: photo.category,
            date: photo.uploadDate ? photo.uploadDate.split('T')[0] : '',
            views: photo.views,
            title: photo.title,
            description: photo.description
          }));
        
        // Check for view count changes and highlight them
        if (!showLoader && photos.length > 0) {
          const newUpdatedViews = new Set();
          formattedPhotos.forEach(newPhoto => {
            const oldPhoto = photos.find(p => p.id === newPhoto.id);
            if (oldPhoto && oldPhoto.views !== newPhoto.views) {
              newUpdatedViews.add(newPhoto.id);
            }
          });
          setUpdatedViews(newUpdatedViews);
          // Clear highlights after 3 seconds
          setTimeout(() => setUpdatedViews(new Set()), 3000);
        }
        
        setPhotos(formattedPhotos);
      }
      if (showLoader) setLoading(false);
    } catch (error) {
      console.error('Error loading photos:', error);
      if (showLoader) setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(getApiUrl('/api/photos/categories'));
      if (response.ok) {
        const categoriesData = await response.json();
        // Filter out "გულშემატკივრები" category from the filter buttons
        const filteredCategories = categoriesData.filter(category => category !== 'გულშემატკივრები');
        setCategories(['ყველა', ...filteredCategories]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleImageClick = async (photo) => {
    setSelectedImage(photo);
    // Increment view count
    try {
      await fetch(getApiUrl(`/api/photos/${photo.id}`));
      // Immediately refresh photos to show updated view count
      setTimeout(() => {
        loadPhotos(false);
      }, 500);
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }


  const filteredPhotos = selectedCategory === 'ყველა' 
    ? photos 
    : photos.filter(photo => photo.category === selectedCategory);

  const totalViews = photos.reduce((sum, photo) => sum + photo.views, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            ფოტო გალერეა
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი გუნდის ყველაზე ლამაზი მომენტები და დაუვიწყარი ფოტოები
          </p>
        </div>

        {/* Gallery Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <PhotoIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">სულ ფოტოები</h3>
            <p className="text-2xl font-bold text-blue-600">{photos.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <TagIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">კატეგორიები</h3>
            <p className="text-2xl font-bold text-green-600">{categories.length - 1}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <EyeIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">სულ ნახვები</h3>
            <p className="text-2xl font-bold text-purple-600">{totalViews.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <CalendarIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">ბოლო განახლება</h3>
            <p className="text-2xl font-bold text-red-600">დღეს</p>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg shadow-lg p-2">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-georgian transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {filteredPhotos.map((photo) => (
            <div
              key={photo.id}
              className="relative group cursor-pointer bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              onClick={() => handleImageClick(photo)}
            >
              <div className="aspect-w-4 aspect-h-3">
                <img
                  src={getImageUrlWithEnv(photo.src)}
                  alt={photo.alt}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <PhotoIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 font-georgian mb-1 text-sm">{photo.title}</h3>
                <p className="text-xs text-gray-600 font-georgian mb-2 line-clamp-2">{photo.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 font-georgian">
                  <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded">{photo.category}</span>
                    <div className={`flex items-center transition-all duration-500 ${
                      updatedViews.has(photo.id) 
                        ? 'text-green-600 font-bold scale-110' 
                        : ''
                    }`}>
                      <EyeIcon className="h-3 w-3 mr-1" />
                      {photo.views}
                    </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        <div className="text-center">
          <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 font-georgian">
            მეტის ჩატვირთვა
          </button>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 z-10 bg-white rounded-full p-2"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              <img
                src={getImageUrlWithEnv(selectedImage.src)}
                alt={selectedImage.alt}
                className="max-w-full max-h-96 w-full object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 font-georgian mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-gray-600 font-georgian mb-4">{selectedImage.description}</p>
                <div className="flex items-center justify-between text-sm text-gray-500 font-georgian">
                  <div className="flex items-center space-x-4">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full">
                      {selectedImage.category}
                    </span>
                    <span className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {selectedImage.date}
                    </span>
                  </div>
                  <span className="flex items-center">
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {selectedImage.views} ნახვა
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="text-center mt-8">
          <Link
            to="/media/social"
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
          >
            ← უკან მედიაზე
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PhotoGallery;
